import classNames from "classnames";
import { useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";

import Dropdown from "src/app/components/dropdown/Dropdown";
import { Icon } from "src/app/components/icon/Icon";
import ItemQtyCell from "src/app/modules/calculator/components/calculation/components/ItemQtyCell";
import { APPLIANCE_THICKNESS_ITEMS, ProjectSystem, SYSTEM_PARAMETER_TYPE, SYSTEM_PARAMETER_TYPE_NAMES } from "src/app/modules/calculator/types";
import placeholderImg from "src/assets/logos/knauf-logo.png";
import { ItemResponse, SystemAdditionalParameterResponse, SystemItemResponse, SystemResponse } from "src/core/models/systems";

import calonCss from "./Calculation.module.css";

function System({
  projectSystem,
  handleRequiredQuantity,
  handleRequiredApplianceThicknessChange,
  handleSystemChange,
}: {
  projectSystem: ProjectSystem | undefined;
  handleRequiredQuantity: (index: number, requiredQuantity: number) => void;
  handleRequiredApplianceThicknessChange: (index: number, requiredApplianceThickness: number) => void;
  handleSystemChange: (index: number, systemId: number) => void;
}) {
  const [requiredQuantity, setRequiredQuantity] = useState<number | undefined>();
  const [requiredApplianceThickness, setRequiredApplianceThickness] = useState<number | undefined>();
  const [applianceThicknessItems, setApplianceThicknessItems] = useState<Array<any>>([]);
  const [detailsTabActive, setDetailsTabActive] = useState(0);

  const mainImageSrc = projectSystem?.system.imagePath != null ? `${process.env.REACT_APP_API_URI}/${projectSystem.system.imagePath}` : placeholderImg;

  const applianceThicknessParam = projectSystem?.system?.additionalParameters?.find(
    (x: SystemAdditionalParameterResponse) => x.parameterType === SYSTEM_PARAMETER_TYPE.ApplianceThickness
  );

  const onRequiredQuantityInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const currentReqQty = requiredQuantity;
    if (Number(event.target.value) <= 1000000) {
      handleRequiredQuantity(projectSystem?.index ?? 0, Number(event.target.value));
    } else {
      setRequiredQuantity(currentReqQty);
    }
  };

  const handleApplianceThicknessChange = (value: number) => {
    handleRequiredApplianceThicknessChange(Number(projectSystem?.index), value);
  };

  const handleSelectChange = (selectedSysId: number) => {
    handleSystemChange(Number(projectSystem?.index), selectedSysId);
  };

  const applyApplianceThickness = (item: ItemResponse) => {
    if (APPLIANCE_THICKNESS_ITEMS.includes(item.code!)) {
      if (requiredApplianceThickness && requiredApplianceThickness !== 0) {
        return requiredApplianceThickness;
      }
    }
    return 1;
  };

  useEffect(() => {
    if (projectSystem?.system && projectSystem?.requiredQuantity >= 0) {
      setRequiredQuantity(projectSystem.requiredQuantity);
    }
  }, [projectSystem, projectSystem?.requiredQuantity]);

  useEffect(() => {
    if (projectSystem?.system && projectSystem?.requiredApplianceThickness >= 0) {
      setRequiredApplianceThickness(projectSystem.requiredApplianceThickness);
    }
  }, [projectSystem, projectSystem?.requiredApplianceThickness]);

  useEffect(() => {
    if (applianceThicknessParam) {
      // populate appliance thickness list
      let res = [];
      for (let i = applianceThicknessParam?.minValue!; i <= applianceThicknessParam?.maxValue!; i++) {
        res.push({
          value: i,
          text: i + " " + applianceThicknessParam?.unitOfMeasurement,
        });
      }
      setApplianceThicknessItems(res);
    }
  }, [applianceThicknessParam]);

  useEffect(() => {
    if (applianceThicknessItems && applianceThicknessItems.length > 0) {
      // set appliance thickness default item
      handleApplianceThicknessChange(applianceThicknessItems[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applianceThicknessItems]);

  return (
    <div>
      {/* Desktop HTML */}
      <div className={classNames("u-pl5per", "u-pr5per", "u-bg-white", calonCss.desktopSystem)}>
        <div className="u-flex u-flex-column">
          <h2 className="u-t-uppercase u-t-primary u-t-align-center">izračun</h2>
          <div className="u-flex u-flex-row u-col-12">
            <img className="u-col-4 u-pr5per u-hfit" alt="velika slika proizvoda" src={mainImageSrc} />
            <div className="u-col-5 u-pr5per u-flex u-flex-column u-flex-content-start">
              <h3 className="u-mb10px u-mt0">{projectSystem?.system.calculationDescription}</h3>
              <div className="u-flex">
                {projectSystem?.system?.descriptions && projectSystem?.system?.descriptions?.length > 0 && (
                  <h6
                    onClick={() => setDetailsTabActive(0)}
                    className={classNames(
                      "u-mt15px",
                      "u-mb0",
                      "u-pt5px",
                      "u-pb5px",
                      "u-pr15px",
                      "u-pl15px",
                      "u-t-w400",
                      "u-t-uppercase",
                      "u-cursor-pointer",
                      detailsTabActive === 0 ? "u-t-primary-dark u-bg-background" : "u-t-secondary"
                    )}
                  >
                    opis sustava
                  </h6>
                )}
                {projectSystem?.system?.applications && projectSystem?.system?.applications?.length > 0 && (
                  <h6
                    onClick={() => setDetailsTabActive(1)}
                    className={classNames(
                      "u-mt15px",
                      "u-mb0",
                      "u-pt5px",
                      "u-pb5px",
                      "u-pr15px",
                      "u-pl15px",
                      "u-t-w400",
                      "u-t-uppercase",
                      "u-cursor-pointer",
                      detailsTabActive === 1 ? "u-t-primary-dark u-bg-background" : "u-t-secondary"
                    )}
                  >
                    područja primjene
                  </h6>
                )}
                {projectSystem?.system?.characteristics && projectSystem?.system?.characteristics?.length > 0 && (
                  <h6
                    onClick={() => setDetailsTabActive(2)}
                    className={classNames(
                      "u-mt15px",
                      "u-mb0",
                      "u-pt5px",
                      "u-pb5px",
                      "u-pr15px",
                      "u-pl15px",
                      "u-t-w400",
                      "u-t-uppercase",
                      "u-cursor-pointer",
                      detailsTabActive === 2 ? "u-t-primary-dark u-bg-background" : "u-t-secondary"
                    )}
                  >
                    karakteristike
                  </h6>
                )}
              </div>
              {projectSystem?.system?.descriptions && (
                <div className={detailsTabActive === 0 ? "u-bg-background u-p10px u-mb15px u-hfit" : "u-none"}>
                  <ul className="u-ml10px u-mr10px u-pl10px u-list-style-none">
                    {projectSystem?.system?.descriptions?.map((descr, index) => (
                      <li key={index} className="u-t-f16p u-tw400 u-mt0 u-mb0">
                        {descr}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {projectSystem?.system?.applications && (
                <div className={detailsTabActive === 1 ? "u-bg-background u-p10px u-mb15px u-hfit" : "u-none"}>
                  <ul className="u-ml10px u-mr10px u-pl10px">
                    {projectSystem?.system?.applications?.map((descr, index) => (
                      <li key={index} className="u-t-f16p u-tw400 u-mt0 u-mb0">
                        {descr}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {projectSystem?.system?.characteristics && (
                <div className={detailsTabActive === 2 ? "u-bg-background u-p10px u-mb15px u-hfit" : "u-none"}>
                  <ul className="u-ml10px u-mr10px u-pl10px">
                    {projectSystem?.system?.characteristics?.map((descr, index) => (
                      <li key={index} className="u-t-f16p u-tw400 u-mt0 u-mb0">
                        {descr}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className={classNames("u-flex", "u-flex-row", "u-mtauto", calonCss.productBtn)}>
                {projectSystem?.system?.technicalDocumentUrl && (
                  <a href={projectSystem.system.technicalDocumentUrl} className="u-cursor-pointer" rel="noreferrer" target="_blank">
                    {projectSystem?.system?.technicalDocumentName}
                  </a>
                )}
                {projectSystem?.system?.videoUrl && (
                  <a href={projectSystem?.system?.videoUrl} className="u-cursor-pointer" rel="noreferrer" target="_blank">
                    Pogledaj video o sustavu
                  </a>
                )}
              </div>
            </div>
            <div className="u-flex u-flex-column u-flex-content-start u-col-3">
              <div className={classNames("u-flex", "u-flex-column", "u-mb5per", calonCss.footageInput)}>
                <label className="u-t-w400 u-t-f14px u-mb10px" htmlFor="requiredQuantity">
                  Unesite potrebnu kvadraturu
                </label>
                <div className="u-flex u-w100 u-flex-items-center u-pos-relative">
                  <input
                    className="u-w100"
                    value={requiredQuantity}
                    type="number"
                    step="0.1"
                    min="0"
                    max="1000000"
                    name="requiredQuantity"
                    onChange={onRequiredQuantityInputChange}
                    placeholder="upisite kvadraturu"
                  />
                  <span className={calonCss.requiredQuantity}>
                    m<sup>2</sup>
                  </span>
                </div>
              </div>
              {applianceThicknessParam && (
                <div className={classNames("u-flex", "u-flex-column", "u-mb5per", calonCss.thicknessInput)}>
                  <label className="u-t-w400 u-t-f14px u-mb10px" htmlFor="additionalParam">
                    {SYSTEM_PARAMETER_TYPE_NAMES.find((type) => type.id === applianceThicknessParam.parameterType)?.text}
                  </label>
                  <div className="u-flex u-w100 u-flex-items-center u-pos-relative">
                    <Dropdown
                      items={applianceThicknessItems}
                      activeItemValue={requiredApplianceThickness}
                      defaultItem={null}
                      placeholder={"odaberite debljinu nanosenja"}
                      removePlaceholder={true}
                      handleChange={(value: number) => {
                        handleApplianceThicknessChange(value);
                      }}
                      customCss={"u-border-secondary"}
                    />
                  </div>
                </div>
              )}
              {projectSystem?.system?.parameterType !== SYSTEM_PARAMETER_TYPE.None && projectSystem?.allSystems && (
                <div className={classNames("u-flex", "u-flex-column", calonCss.thicknessInput)}>
                  <label className="u-t-w400 u-t-f14px u-mb10px" htmlFor="thickness">
                    {SYSTEM_PARAMETER_TYPE_NAMES.find((type) => type.id === projectSystem?.system?.parameterType)?.text}
                  </label>
                  <Dropdown
                    items={projectSystem?.allSystems?.map((system: SystemResponse) => {
                      let value = system.id;
                      let text = `${system.parameterValue}`;
                      return { value: value, text: text };
                    })}
                    activeItemValue={projectSystem?.system.id}
                    defaultItem={null}
                    placeholder={"svi partneri"}
                    removePlaceholder={false}
                    handleChange={(sysId: number) => {
                      handleSelectChange(sysId);
                    }}
                    customCss={""}
                  />
                </div>
              )}
              <div>
                <p className={classNames("u-t-w400 u-t-primary u-t-f14p u-t-lh20 u-mt25px", calonCss.remark)}>
                  {projectSystem?.system.remark?.replace(/\\n/g, "\n")}{" "}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="u-pt40px">
          {/* Table start */}
          <div className={classNames("u-w100", "u-border-collapse", calonCss.productTable)}>
            <div className="u-col-12 u-flex">
              <div className="u-col-2 u-t-align-start u-pb20px"></div>
              <div className="u-col-6 u-t-align-start u-pb20px u-t-secondary u-t-uppercase">naziv proizvoda</div>
              <div className="u-col-2 u-t-align-center u-pb20px u-t-secondary u-t-uppercase">potrebna količina</div>
              <div className="u-col-2 u-t-align-center u-pb20px u-t-secondary u-t-uppercase">proračunata količina</div>
            </div>
            <div className={classNames("u-col-12", "u-flex", "u-flex-column", calonCss.itemsContainer)}>
              {projectSystem?.system.systemItems &&
                projectSystem?.system.systemItems
                  .sort((a: SystemItemResponse, b: SystemItemResponse) => (a.item?.order && b.item?.order && a.item.order < b.item.order ? -1 : 1))
                  .map((systemItem: any, index) => (
                    <ItemRow
                      key={index}
                      itemDifference={projectSystem.itemDifference}
                      systemItem={systemItem}
                      requiredQuantity={requiredQuantity}
                      requiredApplianceThickness={requiredApplianceThickness}
                      applyApplianceThickness={applyApplianceThickness}
                    />
                  ))}
            </div>
          </div>
          {/* Table end */}
        </div>
      </div>
      {/* Mobile HTML */}
      <div className={classNames(calonCss.mobileSystem)}>
        <div className="u-pl5per u-mb10px u-col-xs-11">
          <h3 className="u-mb10px u-mt0">{projectSystem?.system.calculationDescription}</h3>
          <div className="u-flex">
            {projectSystem?.system?.descriptions && projectSystem?.system?.descriptions?.length > 0 && (
              <h6
                onClick={() => setDetailsTabActive(0)}
                className={classNames(
                  "u-mt15px",
                  "u-mb0",
                  "u-pt5px",
                  "u-pb5px",
                  "u-pr15px",
                  "u-pl15px",
                  "u-t-w400",
                  "u-t-uppercase",
                  "u-cursor-pointer",
                  "u-t-primary-dark",
                  "u-t-uppercase",
                  "u-mt5per",
                  detailsTabActive === 0 ? "u-t-primary-dark u-bg-background" : "u-t-secondary"
                )}
              >
                opis sustava
              </h6>
            )}
            {projectSystem?.system?.applications && projectSystem?.system?.applications?.length > 0 && (
              <h6
                onClick={() => setDetailsTabActive(1)}
                className={classNames(
                  "u-mt15px",
                  "u-mb0",
                  "u-pt5px",
                  "u-pb5px",
                  "u-pr15px",
                  "u-pl15px",
                  "u-t-w400",
                  "u-t-uppercase",
                  "u-cursor-pointer",
                  "u-t-primary-dark",
                  "u-t-w400",
                  "u-t-uppercase",
                  "u-mt5per",
                  detailsTabActive === 1 ? "u-t-primary-dark u-bg-background" : "u-t-secondary"
                )}
              >
                područja primjene
              </h6>
            )}
            {projectSystem?.system?.characteristics && projectSystem?.system?.characteristics?.length > 0 && (
              <h6
                onClick={() => setDetailsTabActive(2)}
                className={classNames(
                  "u-mt15px",
                  "u-mb0",
                  "u-pt5px",
                  "u-pb5px",
                  "u-pr15px",
                  "u-pl15px",
                  "u-t-w400",
                  "u-t-uppercase",
                  "u-cursor-pointer",
                  "u-t-primary-dark",
                  "u-t-w400",
                  "u-t-uppercase",
                  "u-mt5per",
                  detailsTabActive === 2 ? "u-t-primary-dark u-bg-background" : "u-t-secondary"
                )}
              >
                karakteristike
              </h6>
            )}
          </div>
          {projectSystem?.system?.descriptions && (
            <div className={detailsTabActive === 0 ? "u-bg-background u-p10px u-mb15px u-h100" : "u-none"}>
              <ul className="u-mr10px u-pl10px u-list-style-none">
                {projectSystem?.system?.descriptions?.map((descr, index) => (
                  <li key={index} className="u-t-f16p u-tw400 u-mt0 u-mb0">
                    {descr}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {projectSystem?.system?.applications && (
            <div className={detailsTabActive === 1 ? "u-bg-background u-p10px u-mb15px u-h100" : "u-none"}>
              <ul className="u-ml10px u-mr10px u-pl10px">
                {projectSystem?.system?.applications?.map((descr, index) => (
                  <li key={index} className="u-t-f16p u-tw400 u-mt0 u-mb0">
                    {descr}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {projectSystem?.system?.characteristics && (
            <div className={detailsTabActive === 2 ? "u-bg-background u-p10px u-mb15px u-h100" : "u-none"}>
              <ul className="u-ml10px u-mr10px u-pl10px">
                {projectSystem?.system?.characteristics?.map((descr, index) => (
                  <li key={index} className="u-t-f16p u-tw400 u-mt0 u-mb0">
                    {descr}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className={classNames("u-flex", "u-flex-row", "u-mtauto", calonCss.productBtn)}>
            {projectSystem?.system?.technicalDocumentUrl && (
              <a href={projectSystem.system.technicalDocumentUrl} className="u-cursor-pointer" rel="noreferrer" target="_blank">
                {projectSystem?.system?.technicalDocumentName}
              </a>
            )}
            {projectSystem?.system?.videoUrl && (
              <a href={projectSystem?.system?.videoUrl} className="u-cursor-pointer" rel="noreferrer" target="_blank">
                Pogledaj video o sustavu
              </a>
            )}
          </div>
          <img className="u-mt5per u-mb5per u-col-xs-12" alt="mala slika proizvoda" src={mainImageSrc} />
        </div>
        <div className="u-pl5per u-flex u-flex-column u-col-xs-11">
          <div className={classNames("u-flex", "u-flex-column", "u-mb30px", calonCss.footageInput)}>
            <label className="u-t-w400 u-t-f14px u-mb10px" htmlFor="requiredQuantity">
              Unesite potrebnu kvadraturu
            </label>
            <div className="u-flex u-w100 u-flex-items-center">
              <input
                className="u-w100"
                value={requiredQuantity}
                type="number"
                step="0.1"
                min="0"
                max="1000000"
                name="requiredQuantity"
                onChange={onRequiredQuantityInputChange}
                placeholder="upisite kvadraturu"
              />
              <span className={calonCss.requiredQuantity}>
                m<sup>2</sup>
              </span>
            </div>
          </div>
          {applianceThicknessParam && (
            <div className={classNames("u-flex", "u-flex-column", "u-mb30px", calonCss.thicknessInput)}>
              <label className="u-t-w400 u-t-f14px u-mb10px" htmlFor="additionalParam">
                {SYSTEM_PARAMETER_TYPE_NAMES.find((type) => type.id === applianceThicknessParam.parameterType)?.text}
              </label>
              <div className="u-flex u-w100 u-flex-items-center">
                <Dropdown
                  items={applianceThicknessItems}
                  activeItemValue={requiredApplianceThickness}
                  defaultItem={applianceThicknessItems[0]}
                  placeholder={"odaberite debljinu nanosa"}
                  removePlaceholder={true}
                  handleChange={(value: number) => {
                    handleApplianceThicknessChange(value);
                  }}
                  customCss={"u-border-secondary"}
                />
              </div>
            </div>
          )}
          {projectSystem?.system?.parameterType !== SYSTEM_PARAMETER_TYPE.None && projectSystem?.allSystems && (
            <div className={classNames("u-flex", "u-flex-column", "u-mb30px", calonCss.thicknessInput)}>
              <label className="u-t-w400 u-t-f14px u-mb10px" htmlFor="thickness">
                {SYSTEM_PARAMETER_TYPE_NAMES.find((type) => type.id === projectSystem?.system?.parameterType)?.text}
              </label>
              <div className="u-flex u-w100 u-flex-items-center">
                <Dropdown
                  items={projectSystem?.allSystems?.map((system: SystemResponse) => {
                    let value = system.id;
                    let text = `${system.parameterValue}`;
                    return { value: value, text: text };
                  })}
                  activeItemValue={projectSystem?.system.id}
                  defaultItem={null}
                  placeholder={"svi partneri"}
                  removePlaceholder={false}
                  handleChange={(sysId: number) => {
                    handleSelectChange(sysId);
                  }}
                  customCss={"u-border-secondary"}
                />
              </div>
            </div>
          )}
          <h6 className="u-t-w400 u-t-primary-dark u-t-f14p u-t-lh20 u-mt0 u-mb25px">
            Za sustave zidova su dane standardne dužine profila. Odaberite dužinu profila koji odgovara vašim zahtjevima/potrebama.
          </h6>
        </div>
        <div className={classNames("u-col-xs-12", "u-flex", "u-flex-content-center", "u-mb50px", calonCss.listMobile)}>
          <ul className="u-col-xs-12 u-pl5per u-pr5per">
            {projectSystem?.system.systemItems &&
              projectSystem?.system.systemItems
                .sort((a: SystemItemResponse, b: SystemItemResponse) => (a.item?.order && b.item?.order && a.item.order < b.item.order ? -1 : 1))
                .map((systemItem: any, index) => (
                  <ItemListItem
                    key={index}
                    systemItem={systemItem}
                    itemDifference={projectSystem.itemDifference}
                    requiredQuantity={requiredQuantity}
                    requiredApplicationThickness={requiredApplianceThickness}
                    applyApplianceThickness={applyApplianceThickness}
                  />
                ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default System;

const ItemRow = ({
  systemItem,
  itemDifference,
  requiredQuantity,
  requiredApplianceThickness,
  applyApplianceThickness,
}: {
  systemItem: SystemItemResponse;
  itemDifference: Array<SystemItemResponse>;
  requiredQuantity: number | undefined;
  requiredApplianceThickness: number | undefined;
  applyApplianceThickness: (item: ItemResponse) => number;
}) => {
  const [active, setActive] = useState(false);
  const [animateActive, setAnimateActive] = useState(false);

  const [alreadyAnimated, setAlreadyAnimated] = useState(false);

  const item = systemItem.item;
  const itemImageSrc = item?.imagePath != null ? `${process.env.REACT_APP_API_URI}/${item?.imagePath}` : placeholderImg;
  const baseQty = systemItem && systemItem.baseQuantity != null ? systemItem.baseQuantity : 1;
  const unitOfMeasurement = item && item?.unitOfMeasurement != null ? item?.unitOfMeasurement : "";
  const isNewItem = itemDifference.some((itemResp) => itemResp.item?.id === item?.id);
  useEffect(() => {
    if (active) {
      setAlreadyAnimated(true);
    }
  }, [active]);

  const changeActiveState = () => {
    if (active) {
      setAnimateActive(false);
      setTimeout(() => {
        setActive(false);
      }, 250);
    } else {
      setAnimateActive(true);
      setActive(true);
    }
  };
  return (
    <AnimateHeight duration={250} className="u-mb10px" height={!animateActive ? 81 : "auto"}>
      {!active ? (
        <div
          key={item?.id}
          className={classNames(
            "u-col-12",
            "u-flex",
            "u-flex-items-center",
            "u-pb10px",
            calonCss.itemContainer,
            isNewItem && !alreadyAnimated ? calonCss.highlightNew : ""
          )}
        >
          <div className={classNames("u-col-2", "u-md-col-3", "u-pos-relative", calonCss.expandCell)}>
            <span
              onClick={() => changeActiveState()}
              className={classNames(
                "u-cursor-pointer",
                "u-bg-secondary-light",
                "u-flex",
                "u-flex-items-center",
                "u-flex-content-center",
                "u-pos-absolute",
                calonCss.expandBtn
              )}
            >
              <Icon name="plus" className="u-t-primary u-t-f12p" />
            </span>
            <div className={calonCss.itemImageCont}>
              <img className={calonCss.itemImage} alt="prozivod manji" src={itemImageSrc}></img>
            </div>
          </div>
          <div className="u-col-6 u-md-col-5 u-t-w700 u-t-f16p">{item?.name}</div>
          <ItemQtyCell
            quantity={(baseQty * applyApplianceThickness(item!)).toFixed(1)}
            unitOfMeasurement={unitOfMeasurement}
            expanded={active}
            mobile={false}
            isWiderCol={false}
          />
          <ItemQtyCell
            quantity={(baseQty * (requiredQuantity ?? 0) * applyApplianceThickness(item!)).toFixed(1)}
            unitOfMeasurement={unitOfMeasurement}
            expanded={active}
            mobile={false}
            isWiderCol={false}
          />
        </div>
      ) : (
        <div key={item?.id} className="u-col-12">
          <div className={classNames("u-col-12", "u-pb10px", calonCss.itemContainer, calonCss.expandCont)}>
            <div className="u-col-12 u-flex u-pos-relative">
              <span
                onClick={() => changeActiveState()}
                className={classNames(
                  "u-cursor-pointer",
                  "u-bg-secondary-light",
                  "u-flex",
                  "u-flex-items-center",
                  "u-flex-content-center",
                  "u-pos-absolute",
                  calonCss.expandBtnLarge
                )}
              >
                <Icon name="plus" className={classNames("u-t-primary u-t-f12p", calonCss.collapseIcon)} />
              </span>
              <div className="u-col-12 u-flex u-flex-column">
                <div className="u-flex u-col-12">
                  <div className="u-col-8">
                    <div className={classNames("u-ml30px", "u-t-primary", "u-t-w700", "u-t-f18p", calonCss.expandName)}>{item?.name}</div>
                  </div>
                  <ItemQtyCell quantity={baseQty.toFixed(1)} unitOfMeasurement={unitOfMeasurement} expanded={active} mobile={false} isWiderCol={false} />
                  <ItemQtyCell
                    quantity={(baseQty * (requiredQuantity ?? 0) * applyApplianceThickness(item!)).toFixed(1)}
                    unitOfMeasurement={unitOfMeasurement}
                    expanded={active}
                    mobile={false}
                    isWiderCol={false}
                  />
                </div>
                <div className="u-flex u-col-12 u-bg-background">
                  <div className={classNames("u-col-3", "u-pl30px", calonCss.expandImgCont)}>
                    <img className={calonCss.itemImageLarge} alt="prozivod veći" src={itemImageSrc}></img>
                  </div>
                  {item?.descriptions && (
                    <div className={classNames("u-col-7", calonCss.descriptionCont, calonCss.contBorder)}>
                      <h5 className="u-t-f16p u-t-w400 u-t-primary u-mb5px u-mt0 u-t-uppercase">Opis Proizvoda:</h5>
                      {item?.descriptions?.map((descr) => (
                        <p className="u-t-f16p u-t-w400 u-m0">{descr}</p>
                      ))}
                    </div>
                  )}
                  <div className={classNames("u-col-2", "u-flex", "u-flex-column", calonCss.descriptionCont)}>
                    <h5 className="u-t-f16p u-t-w400 u-t-primary u-mb5px u-mt0 u-t-uppercase">Šifra Proizvoda:</h5>
                    <p className="u-t-f16p u-t-w400 u-m0 u-mb20px">{item?.code}</p>
                    <h5 className="u-t-f16p u-t-w400 u-t-primary u-mb5px u-mt0 u-t-uppercase">Materijal:</h5>
                    <p className="u-t-f16p u-t-w400 u-m0">{item?.material}</p>
                    {item?.detailsUrl && (
                      <a
                        href={item.detailsUrl}
                        rel="noreferrer"
                        target="_blank"
                        className="u-mtauto u-t-f16p u-t-w400 u-t-primary u-t-underline u-cursor-pointer"
                      >
                        više o proizvodu
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AnimateHeight>
  );
};

const ItemListItem = ({
  systemItem,
  itemDifference,
  requiredQuantity,
  requiredApplicationThickness,
  applyApplianceThickness,
}: {
  systemItem: SystemItemResponse;
  itemDifference: Array<SystemItemResponse>;
  requiredQuantity: number | undefined;
  requiredApplicationThickness: number | undefined;
  applyApplianceThickness: (item: ItemResponse) => number;
}) => {
  const [active, setActive] = useState(false);
  const [animateActive, setAnimateActive] = useState(false);
  const [alreadyAnimated, setAlreadyAnimated] = useState(false);

  const item = systemItem.item;
  const itemImageSrc = item?.imagePath != null ? `${process.env.REACT_APP_API_URI}/${item?.imagePath}` : placeholderImg;
  const baseQty = systemItem && systemItem.baseQuantity != null ? systemItem.baseQuantity : 1;
  const unitOfMeasurement = item && item?.unitOfMeasurement != null ? item?.unitOfMeasurement : "";
  const isNewItem = itemDifference.some((itemResp) => itemResp.item?.id === item?.id);

  useEffect(() => {
    if (active) {
      setAlreadyAnimated(true);
    }
  }, [active]);

  const changeActiveState = () => {
    if (active) {
      setAnimateActive(false);
      setTimeout(() => {
        setActive(false);
      }, 250);
    } else {
      setAnimateActive(true);
      setActive(true);
    }
  };
  return (
    <AnimateHeight duration={250} className="u-mb10px" height={!animateActive ? 160 : "auto"}>
      {!active ? (
        <li key={item?.id} className={classNames("u-pb10px", isNewItem && !alreadyAnimated ? calonCss.highlightNew : "")}>
          <div className="u-flex u-flex-row u-col-xs-12">
            <span
              onClick={() => changeActiveState()}
              className={classNames(
                "u-cursor-pointer",
                "u-bg-secondary-light",
                "u-flex",
                "u-flex-items-center",
                "u-flex-content-center",
                "u-pos-absolute",
                "u-top0",
                "u-h100",
                calonCss.expandBtn
              )}
            >
              <Icon name="plus" className="u-t-primary u-t-f12p" />
            </span>
            <div className="u-ml30px u-w100">
              <div className="u-flex u-flex-row">
                <img className={calonCss.itemImage} alt="prozivod manji" src={itemImageSrc}></img>
                <div className="u-ml10px">
                  <h6 className={classNames("u-mt0", "u-mb0", "u-col-xs-12", "u-t-w400", calonCss.itemNameMobile)}>{item?.name}</h6>
                </div>
              </div>
              <div className="u-w100">
                <table className="u-w100">
                  <tbody>
                    <tr className="u-t-w700">
                      <td className="u-col-9 u-pl10px u-t-secondary u-t-f16p u-t-lh15 u-t-w400">
                        Potrebna količina na m<sup>2</sup>:
                      </td>
                      <ItemQtyCell quantity={baseQty.toFixed(1)} unitOfMeasurement={unitOfMeasurement} expanded={active} mobile={true} isWiderCol={false} />
                    </tr>
                  </tbody>
                </table>
                <table className="u-w100 u-p0">
                  <tbody>
                    <tr className="u-t-w700">
                      <td className="u-col-7 u-pl10px u-t-secondary u-t-f16p u-t-lh15 u-t-w400">Proračunata količina:</td>
                      <ItemQtyCell
                        quantity={(baseQty * (requiredQuantity ?? 0) * applyApplianceThickness(item!)).toFixed(1)}
                        unitOfMeasurement={unitOfMeasurement}
                        expanded={active}
                        mobile={true}
                        isWiderCol={true}
                      />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </li>
      ) : (
        <li key={item?.id} className="u-pb10px">
          <div className={classNames("u-col-12", "u-flex", "u-pos-relative", "u-flex-wrap", calonCss.expandCont)}>
            <span
              onClick={() => changeActiveState()}
              className={classNames(
                "u-cursor-pointer",
                "u-bg-secondary-light",
                "u-flex",
                "u-flex-items-center",
                "u-flex-content-center",
                "u-pos-absolute",
                calonCss.expandBtnLarge
              )}
            >
              <Icon name="plus" className={classNames("u-t-primary u-t-f12p", calonCss.collapseIcon)} />
            </span>
            <div className="u-ml30px u-w100">
              <div className="u-flex u-flex-row">
                <img className={calonCss.itemImage} alt="prozivod manji" src={itemImageSrc}></img>
                <div className="u-ml10px">
                  <h6 className="u-mt0 u-mb0 u-t-w400 u-col-xs-12">{item?.name}</h6>
                </div>
              </div>
              <div className="u-w100">
                <table className="u-w100">
                  <tbody>
                    <tr className="u-t-w700">
                      <td className="u-col-9 u-pl10px u-t-secondary u-t-f16p u-t-lh15 u-t-w400">
                        Potrebna količina na m<sup>2</sup>:
                      </td>
                      <ItemQtyCell quantity={baseQty.toFixed(1)} unitOfMeasurement={unitOfMeasurement} expanded={active} mobile={true} isWiderCol={false} />
                    </tr>
                  </tbody>
                </table>
                <table className="u-w100 u-p0">
                  <tbody>
                    <tr className="u-t-w700">
                      <td className="u-col-7 u-pl10px u-t-secondary u-t-f16p u-t-lh15 u-t-w400">Proračunata količina:</td>
                      <ItemQtyCell
                        quantity={(baseQty * (requiredQuantity ?? 0) * applyApplianceThickness(item!)).toFixed(1)}
                        unitOfMeasurement={unitOfMeasurement}
                        expanded={active}
                        mobile={true}
                        isWiderCol={true}
                      />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="u-col-12 u-ml30px u-flex u-flex-column u-bg-background">
              {item?.descriptions && (
                <div className={classNames("u-wauto", "u-pr10px", "u-mt20px", calonCss.descriptionCont)}>
                  <h5 className="u-t-f16p u-t-w400 u-t-primary u-mb5px u-mt0 u-t-uppercase">Opis Proizvoda:</h5>
                  {item?.descriptions?.map((descr) => (
                    <p className="u-t-f16p u-t-w400 u-m0">{descr}</p>
                  ))}
                </div>
              )}
              <div className={classNames("u-col-12", "u-flex", "u-flex-column", "u-flex-items-start", calonCss.descriptionCont)}>
                <h5 className="u-t-f16p u-t-w400 u-t-primary u-mb5px u-mt0 u-t-uppercase">Šifra Proizvoda:</h5>
                <p className="u-t-f16p u-t-w400 u-m0 u-mb20px">{item?.code}</p>
                <h5 className="u-t-f16p u-t-w400 u-t-primary u-mb5px u-mt0 u-t-uppercase">Materijal:</h5>
                <p className="u-t-f16p u-t-w400 u-m0">{item?.material}</p>
              </div>
              <div className={classNames("u-col-12", "u-flex", "u-flex-items-end", "u-flex-content-center", "u-ml0", "u-mt10px", "u-mb30px")}>
                {item?.detailsUrl && (
                  <a href={item.detailsUrl} rel="noreferrer" target="_blank" className="u-t-f16p u-t-w400 u-t-primary u-t-underline u-cursor-pointer">
                    više o proizvodu
                  </a>
                )}
              </div>
            </div>
          </div>
        </li>
      )}
    </AnimateHeight>
  );
};
